import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../api';

interface CompanyContextType {
  memoryEnabled: boolean;
  setMemoryEnabled: (enabled: boolean) => void;
  hasSubscription: boolean;
  setHasSubscription: (has: boolean) => void;
  aiTimeMinutes: number;
  setAiTimeMinutes: (mins: number) => void;
  resetsAt: string | null;
  setResetsAt: (date: string | null) => void;
  hasPersonalAssistant: boolean;
  setHasPersonalAssistant: (has: boolean) => void;
  isOpsInternalUser: boolean;
  opsRole: 'ops_worker' | 'ops_admin' | null;
  // Full name stored on our `users` table (not Auth0's nickname, which is
  // derived from the email local-part and can look like `ben+prod`).
  userName: string | null;
  loading: boolean;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const CompanyProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [memoryEnabled, setMemoryEnabled] = useState<boolean>(false);
  const [hasSubscription, setHasSubscription] = useState<boolean>(false);
  const [aiTimeMinutes, setAiTimeMinutes] = useState<number>(0);
  const [resetsAt, setResetsAt] = useState<string | null>(null);
  const [hasPersonalAssistant, setHasPersonalAssistant] =
    useState<boolean>(false);
  const [isOpsInternalUser, setIsOpsInternalUser] = useState<boolean>(false);
  const [opsRole, setOpsRole] = useState<'ops_worker' | 'ops_admin' | null>(
    null,
  );
  const [userName, setUserName] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchUserDetails = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      const response = await api.get('/user-details', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsOpsInternalUser(response.data.isOpsInternalUser || false);
      setOpsRole(response.data.opsRole || null);
      setUserName(
        typeof response.data.name === 'string' && response.data.name.trim()
          ? response.data.name.trim()
          : null,
      );

      // Capture browser fingerprint for Dream Browser proxy/context mirroring.
      // Fire-and-forget — failure here is non-critical.
      api
        .post(
          '/browser-fingerprint',
          {
            userAgent: navigator.userAgent,
            locale: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            languages: Array.from(navigator.languages),
            platform: navigator.platform,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .catch((err: unknown) =>
          console.warn('Failed to send browser fingerprint:', err),
        );
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  return (
    <CompanyContext.Provider
      value={{
        memoryEnabled,
        setMemoryEnabled,
        hasSubscription,
        setHasSubscription,
        aiTimeMinutes,
        setAiTimeMinutes,
        resetsAt,
        setResetsAt,
        hasPersonalAssistant,
        setHasPersonalAssistant,
        isOpsInternalUser,
        opsRole,
        userName,
        loading,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = (): CompanyContextType => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};
